import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AVP_SHIFTS } from "../lib/avpShifts";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Car, ShieldCheck, Save } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import DisclosureBanner from "../components/DisclosureBanner";
import { StepContact, StepEligibility, StepAvailability } from "./avp/StepsDriver";
import { StepVehicleAccess } from "./avp/StepsVehicle";
import { StepAcknowledge, ACKS } from "./avp/StepsFinal";

const STORAGE_KEY = "idi_avp_application_v2";
const EMPTY = { contact: {}, eligibility: {}, availability: { days: [] }, vehicle_access: {}, vehicle: {}, pickup: {}, documents: {}, physical: {}, acknowledgments: {}, signature: "", signature_date: new Date().toISOString().slice(0, 10) };

const filled = (obj, keys) => keys.every((k) => obj[k] !== undefined && obj[k] !== null && String(obj[k]).trim() !== "");

export default function ApplyAvp() {
  const [form, setForm] = useState(() => {
    try { const s = localStorage.getItem(STORAGE_KEY); return s ? { ...EMPTY, ...JSON.parse(s).form } : EMPTY; } catch { return EMPTY; }
  });
  const [step, setStep] = useState(() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}").step || 0; } catch { return 0; } });
  const [restored] = useState(() => !!localStorage.getItem(STORAGE_KEY));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [sp] = useSearchParams();
  useEffect(() => { const s = sp.get("shift"); if (s && AVP_SHIFTS[s]) setForm((f) => ({ ...f, availability: { ...f.availability, shift: s } })); }, [sp]);

  useEffect(() => { if (!result) localStorage.setItem(STORAGE_KEY, JSON.stringify({ form, step, saved_at: Date.now() })); }, [form, step, result]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const setSec = (sec) => (k, v) => setForm((f) => ({ ...f, [sec]: { ...f[sec], [k]: v } }));
  const setAck = (k, v) => setForm((f) => ({ ...f, acknowledgments: { ...f.acknowledgments, [k]: v } }));

  const steps = useMemo(() => [
    { key: "contact", title: "Contact", valid: () => filled(form.contact, ["full_name", "phone", "email", "street", "city", "state", "zip"]) },
    { key: "eligibility", title: "Qualifications", valid: () => filled(form.eligibility, ["is_21", "license_2yr", "license_state", "license_expiration", "willing_mvr_background", "willing_testing", "has_experience", "prior_fedex"]) },
    { key: "availability", title: "Shift & Availability", valid: () => (form.availability.days || []).length > 0 && filled(form.availability, ["shift", "midday_10_2", "earliest_start", "latest_end", "baltimore_area"]) },
    { key: "vehicle_access", title: "Vehicle Access", valid: () => {
      const a = form.vehicle_access;
      if (!a.has_vehicle) return false;
      if (a.has_vehicle === "no") return true;
      if (!a.ownership || !a.vehicle_available_all_times) return false;
      if (a.ownership === "other_person" && !a.owner_permission) return false;
      if (a.ownership === "rented" && !a.can_provide_rental_agreement) return false;
      return true;
    } },
    { key: "ack", title: "Sign", valid: () => ACKS.every(([k]) => form.acknowledgments[k]) && form.signature.trim().length >= 2 && !!form.signature_date },
  ], [form]);

  const idx = Math.min(step, steps.length - 1);
  const cur = steps[idx];
  const pct = Math.round(((idx + 1) / steps.length) * 100);
  const canNext = cur.valid();

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/avp-applications", { ...form, position: AVP_SHIFTS[form.availability.shift]?.position || "avp_priority" });
      localStorage.removeItem(STORAGE_KEY);
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const reset = () => { localStorage.removeItem(STORAGE_KEY); setForm(EMPTY); setStep(0); };

  if (result) {
    return (
      <div>
        <DisclosureBanner />
        <section className="py-20">
          <div data-testid="avp-success" className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-emerald-400" /></div>
            <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold uppercase display text-white">AVP application received</h1>
            <p className="mt-4 text-slate-300 leading-relaxed">Thank you, {form.contact.full_name.split(" ")[0]}. We've e-mailed AVP program information to <strong className="text-white">{form.contact.email}</strong>. Please open it and <strong className="text-white">confirm your interest and preferred shift</strong> — that places you in our management review queue.</p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-200"><ShieldCheck className="w-4 h-4 text-amber-400" /> Next step: <strong data-testid="avp-success-status">Confirm interest by e-mail</strong></div>
            <p className="mt-6 text-xs text-slate-500">No vehicle documents are needed right now. If management moves forward, we'll ask for your vehicle details and then send a separate secure upload link.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/"><Button variant="outline" className="border-slate-700 text-slate-200 w-full">Back to home</Button></Link>
              <Link to="/careers"><Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold w-full">View other openings</Button></Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <DisclosureBanner />
      <section className="py-10 md:py-14 border-b border-slate-900 gradient-radial">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><Car className="w-4 h-4" /> Careers · Own-Vehicle Program</div>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-white display">AVP Initial Application</h1>
          <p data-testid="avp-intro" className="mt-5 text-slate-300 leading-relaxed text-sm sm:text-base">
            A short first step (about 5 minutes): contact details, basic qualifications, your preferred shift, and whether you have access to a qualifying vehicle. Vehicle details and documents are only requested later if management decides to move forward. Completing this application does not guarantee employment or vehicle approval.
          </p>
          {restored && idx > 0 && <div data-testid="avp-restored-note" className="mt-4 inline-flex items-center gap-2 text-xs text-emerald-300"><Save className="w-3.5 h-3.5" /> Welcome back — your progress was saved on this device.</div>}
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span data-testid="avp-step-indicator">Step {idx + 1} of {steps.length} · {cur.title}</span>
            <span>{pct}%</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-800 overflow-hidden"><div data-testid="avp-progress-bar" className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
          <div className="mt-3 hidden sm:flex flex-wrap gap-1.5">
            {steps.map((s, i) => (
              <button key={s.key} type="button" onClick={() => i < idx && setStep(i)} disabled={i > idx} className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${i === idx ? "bg-amber-500 text-slate-950 font-bold" : i < idx ? "text-slate-300 hover:text-amber-400" : "text-slate-600"}`}>{s.title}</button>
            ))}
          </div>

          <div data-testid={`avp-step-${cur.key}`} className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8">
            {cur.key === "contact" && <StepContact d={form.contact} set={setSec("contact")} />}
            {cur.key === "eligibility" && <StepEligibility d={form.eligibility} set={setSec("eligibility")} />}
            {cur.key === "availability" && <StepAvailability d={form.availability} set={setSec("availability")} />}
            {cur.key === "vehicle_access" && <StepVehicleAccess d={form.vehicle_access} set={setSec("vehicle_access")} docs={form.documents} />}
            {cur.key === "ack" && <StepAcknowledge acks={form.acknowledgments} setAck={setAck} signature={form.signature} setSignature={(v) => setForm((f) => ({ ...f, signature: v }))} signatureDate={form.signature_date} setSignatureDate={(v) => setForm((f) => ({ ...f, signature_date: v }))} />}
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <Button data-testid="avp-back-btn" type="button" variant="outline" className="border-slate-700 text-slate-200" disabled={idx === 0} onClick={() => setStep(idx - 1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <div className="flex items-center gap-3">
              <button type="button" data-testid="avp-reset-btn" onClick={reset} className="text-xs text-slate-500 hover:text-rose-400">Start over</button>
              {idx < steps.length - 1 ? (
                <Button data-testid="avp-next-btn" type="button" disabled={!canNext} onClick={() => setStep(idx + 1)} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>
              ) : (
                <Button data-testid="avp-submit-btn" type="button" disabled={!canNext || submitting} onClick={submit} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Submit AVP Application
                </Button>
              )}
            </div>
          </div>
          {!canNext && <div className="mt-2 text-right text-xs text-slate-500">Complete the required fields (*) to continue.</div>}
          <div className="mt-6 text-xs text-slate-500 flex items-center gap-2"><Save className="w-3.5 h-3.5" /> Your progress is saved automatically on this device so you can return later.</div>
        </div>
      </section>
    </div>
  );
}
